import {
  Vector3,
  Quaternion
} from "three"

import nipplejs from "nipplejs"

import { crossfadeAction, inRange, isM } from "../components/alpha"
import { Vec3 } from "cannon-es"

// ------------------------------------------------------------

export class CharacterController {

  #_enabled = false

  #_model
  #_body
  #_mixer
  #_actions
  #_camera

  #_joystick = undefined
  #_joystickData = undefined

  // State
  #_toggleRun = false
  #_currentAction = 'Idle'

  // Temp data
  #_walkDirection = new Vector3()
  #_rotateAxis = new Vector3(0,1,0)
  #_quat = new Quaternion()

  // Consts
  #_fadeDuration = 0.25
  #_walkVelocity = !isM ? 2.5 : 1.5
  #_runVelocity = !isM ? 6 : 4

  //

  #_keyOn = false
  #_keysPressed = {}

  //

  constructor(params) {

    this.#_model = params.avatar.model
    this.#_body = params.avatar.body
    this.#_mixer = params.avatar.mixer
    this.#_actions = params.avatar.actions
    this.#_camera = params.camera

    this.init()
    
  }

  /**
   * @param {boolean} value
   */
  set enabled(value) {

    // Force off
    if(value == false && this.#_joystick) {
      this.#_joystick.off('move')
      this.#_joystick.remove()
      this.#_joystickData = null
      this.#_body.angularVelocity = new Vec3(0,0,0)
      this.switchRunToggle('off')
    }

    setTimeout(() => { this.#_enabled = value }, 5)

  }

  //

  init() {
    // Play IDLE
    this.#_actions[this.#_currentAction].play()

    // Joystick
    const joystick = nipplejs.create({
        zone: document.querySelector('.joystick-container'),
        size: 120,
    })
    
    joystick.on('added', (e, nipple) => {

      if (this.#_enabled) {

        window.cameraController.target = window.avatar.active.model
        window.cameraController.enableFollow = true

        // Wake up body
        this.#_body.applyImpulse( new Vec3(0,0,0.2), new Vec3(0,0,0) )
  
        // Send data
        nipple.on('move', (e, data) => {
          this.#_joystickData = data
          this.#_joystickData.force = Math.min(data.force, 1)
        })
  
        this.#_joystick = nipple
      }

    }).on('removed', (e, nipple) => {
        nipple.off('move')

        // Run off
        this.switchRunToggle('off')

        this.#_joystickData = null
        this.#_body.angularVelocity = new Vec3(0,0,0)
    })

    const keyValidation = (current, value) => {

      const keys = [
        'w', 'a', 's', 'd',
        'arrowup', 'arrowleft', 'arrowdown', 'arrowright'
      ]
      if (keys.includes(current)) {
        this.#_keyOn = true
        this.#_keysPressed[current] = value

        // Wake up body
        this.#_body.applyImpulse( new Vec3(0,0,0.2), new Vec3(0,0,0) )
      }

      if (value === false) {
        this.#_keysPressed = Object.fromEntries(Object.entries(this.#_keysPressed).filter(([_, valor]) => valor));
        
        if (Object.keys(this.#_keysPressed).length === 0) {
          this.#_keyOn = false

          // Run off
          // this.switchRunToggle('off')
        }
      }

    }

    // Keys listener
    document.addEventListener('keydown', (event) => {
      if (event.shiftKey) this.switchRunToggle()

      const current = event.key.toLowerCase()
      keyValidation(current, true)
    }, false)
    document.addEventListener('keyup', (event) => {
      
      const current = event.key.toLowerCase()
      keyValidation(current, false)
    }, false)

    //

    document.addEventListener('touchstart', (e) => {
      if (e.touches.length == 2 ) {

        if (this.#_toggleRun === 'on') return
        this.switchRunToggle('on')
      } else {

        this.switchRunToggle('off')
      }
    })
    
  }

  switchRunToggle(turn) {
    this.#_toggleRun = turn === 'off' ? false : !this.#_toggleRun
  }
  directionOffset(keysPressed) {
    var directionOffset = 0 // s

    const W = 'w'
    const A = 'a'
    const S = 's' 
    const D = 'd' 

    const up = 'arrowup'
    const left = 'arrowleft'
    const down = 'arrowdown'
    const right = 'arrowright'

    if (keysPressed[S] || keysPressed[down]) {
        if (keysPressed[D] || keysPressed[right]) {
            directionOffset = Math.PI / 4 // s+d
        } else if (keysPressed[A] || keysPressed[left]) {
            directionOffset = - Math.PI / 4 // s+a
        }
    } else if (keysPressed[W] || keysPressed[up]) {
        if (keysPressed[D] || keysPressed[right]) {
            directionOffset = Math.PI / 4 + Math.PI / 2 // w+d
        } else if (keysPressed[A] || keysPressed[left]) {
            directionOffset = -Math.PI / 4 - Math.PI / 2 // w+a
        } else {
            directionOffset = Math.PI // w
        }
    } else if (keysPressed[D] || keysPressed[right]) {
        directionOffset = Math.PI / 2 // d
    } else if (keysPressed[A] || keysPressed[left]) {
        directionOffset = - Math.PI / 2 // a
    }

    return directionOffset
  }

  update(deltaRaw) {

    if (this.#_enabled == false) return

    const delta = 1 - Math.pow(0.06, deltaRaw)

    // Handle actions (Mixer updated outside)
    //* Hacer esta valiadacion al mover el joystick y al eliminarlo, no en el ciclo update.
    let action
    if ((this.#_joystickData || this.#_keyOn) && this.#_toggleRun) { action = 'Correr' } else if (this.#_joystickData || this.#_keyOn) { action = 'Trotar' } else { action = 'Idle' }

    if(this.#_currentAction != action) {
      const toPlay = this.#_actions[action]
      const current = this.#_actions[this.#_currentAction]

      crossfadeAction(current, toPlay, this.#_fadeDuration )

      this.#_currentAction = action
    }

    // Handle movement
    if (this.#_currentAction == 'Correr' || this.#_currentAction == 'Trotar') {

      // Calculate towards camera direction
      const angleYCameraDirection = Math.atan2(
        (this.#_camera.position.x - this.#_body.position.x),
        (this.#_camera.position.z - this.#_body.position.z),
      )

      // Offset
      const directionOffset = this.#_keyOn ? this.directionOffset( this.#_keysPressed ) : this.#_joystickData.angle.radian + (Math.PI/2)

      // Rotate model
      this.#_quat.setFromAxisAngle(this.#_rotateAxis, angleYCameraDirection + directionOffset)
      this.#_model.quaternion.rotateTowards(this.#_quat, 0.2)

      // Check is walking front or back
      // window.cameraController.walkingDirection = inRange(directionOffset, (Math.PI/12) * 19, (Math.PI/12) * 29) ? 'Back' : 'Front'

      // Calculate direction
      this.#_camera.getWorldDirection(this.#_walkDirection)
      this.#_walkDirection.y = 0
      this.#_walkDirection.normalize()
      this.#_walkDirection.applyAxisAngle(this.#_rotateAxis, directionOffset)
      

      // Vars
      const velocity = this.#_currentAction == 'Correr' ? this.#_runVelocity : this.#_walkVelocity
      const force = this.#_keyOn ? 1 : this.#_joystickData.force
      const scalar = 100

      const moveX = (this.#_walkDirection.x * velocity * delta * scalar) * force
      const moveZ = (this.#_walkDirection.z * velocity * delta * scalar) * force

      
      // Update body/model
      this.#_body.velocity.set( -moveX, 0, -moveZ)


      // Mixer timescale
      // this.#_mixer.timeScale = force
    }

    // Update model position
    const tempPos = new Vector3().copy(this.#_model.position)
        .lerp( new Vector3( this.#_body.position.x, 0, this.#_body.position.z ), 0.33)
    this.#_model.position.copy( tempPos)

    // window.testLight.position.copy()

    // console.log(this.#_model.position, window.testLight.position)
    // console.log(this.#_model.position.x - 9)
    // const newLightPos = new Vector3(
    //   this.#_model.position.x - 9,
    //   18,
    // )
    const lud = window.testLight.userData
    window.testLight.position.set(
      this.#_model.position.x + (lud.x),
      lud.y,
      this.#_model.position.z + (lud.z)
    )
    window.testLight.target = this.#_model
  }

}